/** Shared attorney autocomplete search helpers (bond creation flows). */

import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";

export const ATTORNEY_MIN_SEARCH_LENGTH = 3;
export const ATTORNEY_SEARCH_DEBOUNCE_MS = 300;

export function shouldRunAttorneySearch(searchQuery) {
  return (searchQuery || "").trim().length >= ATTORNEY_MIN_SEARCH_LENGTH;
}

export function getLawFirmAttorneysEndpointId(role, lawfirmId) {
  if (role === "law_firm_user" || role === "law_firm_admin") {
    return lawfirmId ?? 0;
  }
  return 0;
}

export function buildAttorneySearchParams(searchQuery) {
  return { q: (searchQuery || "").trim() };
}

export function formatAttorneyOption(attorney) {
  return {
    value: attorney.lawfirm_user_id,
    label: attorney.full_name,
    position: attorney.position,
    address: attorney.mailing_address,
    email: attorney.email,
    city: attorney.city,
    state: attorney.state,
    zip_code: attorney.zip_code,
    is_gia_on_file: !!attorney.is_gia_on_file,
    is_loa_on_file: !!attorney.is_loa_on_file,
  };
}

export function getAttorneySearchTimeoutMessage(error) {
  if (error?.code === "ECONNABORTED") {
    return "Attorney search timed out. Please try again.";
  }
  return null;
}

export function getAttorneySearchErrorMessage(error) {
  const timeoutMessage = getAttorneySearchTimeoutMessage(error);
  if (timeoutMessage) {
    return timeoutMessage;
  }
  if (typeof error?.error === "string") {
    return error.error;
  }
  return "Unable to search attorneys. Please try again.";
}

export function getAttorneyAutocompleteNoOptionsText(inputValue, loading) {
  if (loading) {
    return "Searching...";
  }
  if (shouldRunAttorneySearch(inputValue)) {
    return "No attorneys found";
  }
  return "Type at least 3 characters to search for attorneys";
}

export function shouldShowAttorneySearchEmptyState(inputValue, loading, attorneyCount) {
  return (
    !loading &&
    shouldRunAttorneySearch(inputValue) &&
    attorneyCount === 0
  );
}

export function getAttorneySearchFieldHelperText(inputValue, loading, attorneyCount) {
  if (loading) {
    return "Searching attorneys...";
  }
  if (shouldShowAttorneySearchEmptyState(inputValue, loading, attorneyCount)) {
    return "No matching attorneys. Try first or last name only, or enter details manually below.";
  }
  if (!shouldRunAttorneySearch(inputValue)) {
    return "Type at least 3 characters to search.";
  }
  return "";
}

export function applyAttorneyToGuardian(guardian, attorney) {
  return {
    ...guardian,
    name: attorney.label,
    address: attorney.address,
    email: attorney.email,
    city: attorney.city,
    state: attorney.state,
    zip_code: attorney.zip_code,
    is_attorney: "Yes",
    is_attorney_home: "No",
    occupation: attorney.position,
    relationship: "Attorney of Record",
    citizenship: "USA",
    country: "USA",
    attorney: attorney.value,
    showAttorneyForm: false,
  };
}

export function clearAttorneyFromGuardian(guardian) {
  return {
    ...guardian,
    is_attorney: "No",
    is_attorney_home: "No",
    attorney: null,
    name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    occupation: "",
    relationship: "",
  };
}

export function runAttorneyAutocompleteInputChange(reason, value, debouncedSearch) {
  if (reason === "input") {
    debouncedSearch(value);
  }
}

export function buildProbateLawFirmSelectionPayload(
  data,
  newFirmValue,
  lawFirmAddress,
  attorneyOfRecord,
  attorneyInvolved
) {
  return {
    ...data,
    lawFirm: newFirmValue,
    lawFirmAddress,
    attorneyOfRecord,
    attorneyInvolved,
  };
}

export function mergeAttorneyIntoGuardians(guardians, index, attorney) {
  const updatedGuardians = [...guardians];
  updatedGuardians[index] = attorney
    ? applyAttorneyToGuardian(updatedGuardians[index], attorney)
    : clearAttorneyFromGuardian(updatedGuardians[index]);
  return updatedGuardians;
}

export async function fetchAttorneySearchResults(
  apiClient,
  role,
  lawfirmId,
  searchQuery
) {
  const trimmed = (searchQuery || "").trim();
  if (!shouldRunAttorneySearch(trimmed)) {
    return [];
  }
  const id = getLawFirmAttorneysEndpointId(role, lawfirmId);
  const response = await apiClient(
    `/lawfirm-attorneys/${id}`,
    "GET",
    {},
    buildAttorneySearchParams(trimmed)
  );
  return response.map(formatAttorneyOption);
}

export function useAttorneyAutocompleteSearch({
  apiClient,
  role,
  lawfirmId,
  onSearchError,
}) {
  const [attorneys, setAttorneys] = useState([]);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);
  const onSearchErrorRef = useRef(onSearchError);

  useEffect(() => {
    onSearchErrorRef.current = onSearchError;
  }, [onSearchError]);

  const searchAttorneys = useCallback(
    async (searchQuery) => {
      if (!shouldRunAttorneySearch(searchQuery)) {
        setAttorneys([]);
        return;
      }
      setLoadingAttorneys(true);
      try {
        const results = await fetchAttorneySearchResults(
          apiClient,
          role,
          lawfirmId,
          searchQuery
        );
        setAttorneys(results);
      } catch (error) {
        console.error("Error searching attorneys:", error);
        setAttorneys([]);
        onSearchErrorRef.current?.(error);
      } finally {
        setLoadingAttorneys(false);
      }
    },
    [apiClient, role, lawfirmId]
  );

  const debouncedSearchRef = useRef(() => {});

  useEffect(() => {
    debouncedSearchRef.current = debounce(
      (query) => searchAttorneys(query),
      ATTORNEY_SEARCH_DEBOUNCE_MS
    );
    return () => debouncedSearchRef.current.cancel();
  }, [searchAttorneys]);

  const debouncedSearch = useCallback((query) => {
    debouncedSearchRef.current(query);
  }, []);

  const clearAttorneys = useCallback(() => setAttorneys([]), []);

  return {
    attorneys,
    loadingAttorneys,
    debouncedSearch,
    clearAttorneys,
  };
}
